import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { Calendar, LogOut, CalendarDays, Bell, Eye, Trash2, Home } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import PWAInstallPrompt from '../components/PWAInstallPrompt';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const LOGO_URL = 'https://customer-assets.emergentagent.com/job_groom-calendar/artifacts/u8mpsttn_1000064271.jpg';

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newCount, setNewCount] = useState(0);
  
  useEffect(() => {
    fetchBookings();
  }, []);
  
  useEffect(() => {
    const count = bookings.filter(b => b.status === 'new').length;
    setNewCount(count);
  }, [bookings]);
  
  const fetchBookings = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/bookings`, {
        withCredentials: true
      });
      setBookings(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Failed to fetch bookings:', error);
      toast.error('Failed to load bookings');
      setLoading(false);
    }
  };
  
  const handleMarkViewed = async (bookingId) => {
    try {
      await axios.patch(
        `${BACKEND_URL}/api/admin/bookings/${bookingId}/mark-viewed`,
        {},
        { withCredentials: true }
      );
      setBookings(bookings.map(b => 
        b.booking_id === bookingId ? { ...b, status: 'viewed' } : b
      ));
      toast.success('Marked as viewed');
    } catch (error) {
      console.error('Failed to mark as viewed:', error);
      toast.error('Failed to update status');
    }
  };
  
  const handleDeleteBooking = async (bookingId) => {
    if (!window.confirm('Are you sure you want to delete this booking enquiry?')) {
      return;
    }
    
    try {
      await axios.delete(
        `${BACKEND_URL}/api/admin/bookings/${bookingId}`,
        { withCredentials: true }
      );
      setBookings(bookings.filter(b => b.booking_id !== bookingId));
      toast.success('Booking deleted');
    } catch (error) {
      console.error('Failed to delete booking:', error);
      toast.error('Failed to delete booking');
    }
  };
  
  const handleLogout = async () => {
    try {
      await axios.post(`${BACKEND_URL}/api/auth/logout`, {}, { withCredentials: true });
      navigate('/admin/login');
    } catch (error) {
      console.error('Logout error:', error);
      navigate('/admin/login');
    }
  };
  
  const formatDate = (dateStr) => {
    try {
      const date = new Date(dateStr);
      return format(date, 'MMM dd, yyyy');
    } catch {
      return dateStr;
    }
  };
  
  const getServiceColor = (service) => {
    if (service === 'Full Groom') return '#5AC5C1';
    if (service === 'Bath & Brush') return '#F5C1C1';
    return '#B8E5D2';
  };
  
  return (
    <div className="min-h-screen" style={{ backgroundColor: '#F8F9FA' }}>
      <nav className="bg-white shadow-sm border-b" style={{ borderColor: '#D1E5E3' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <img 
              src={LOGO_URL} 
              alt="Fur & Flair Logo" 
              className="h-12 cursor-pointer"
              onClick={() => navigate('/')}
            />
            <div className="flex items-center gap-4">
              {newCount > 0 && (
                <div className="flex items-center gap-2 px-4 py-2 rounded-full" style={{ backgroundColor: '#B8E5D2' }}>
                  <Bell className="w-4 h-4" style={{ color: '#2F5F5F' }} />
                  <span className="font-bold text-sm" style={{ color: '#2F5F5F' }}>
                    {newCount} New {newCount === 1 ? 'Enquiry' : 'Enquiries'}
                  </span>
                </div>
              )}
              <button
                onClick={() => navigate('/')}
                data-testid="nav-home-button"
                className="px-6 py-2 rounded-full font-medium transition-all hover:shadow-md"
                style={{ backgroundColor: '#B8E5D2', color: '#2F5F5F' }}
                title="Back to Booking Page"
              >
                <Home className="w-5 h-5" />
              </button>
              <button
                onClick={() => navigate('/admin/calendar')}
                data-testid="nav-calendar-button"
                className="px-6 py-2 rounded-full font-medium transition-all hover:shadow-md"
                style={{ backgroundColor: '#F5C1C1', color: 'white' }}
              >
                <CalendarDays className="w-5 h-5" />
              </button>
              <button
                onClick={handleLogout}
                data-testid="logout-button"
                className="px-6 py-2 rounded-full font-medium transition-all hover:shadow-md"
                style={{ backgroundColor: '#E5F2F1', color: '#2F5F5F' }}
              >
                <LogOut className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </nav>
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <h2 className="text-3xl font-bold mb-6" style={{ fontFamily: 'Nunito, sans-serif', color: '#2F5F5F' }}>
          Booking Enquiries
        </h2>
        
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          </div>
        ) : bookings.length === 0 ? (
          <div className="bg-white rounded-3xl p-12 text-center shadow-sm">
            <Calendar className="w-16 h-16 mx-auto mb-4" style={{ color: '#6B7280' }} />
            <p className="text-xl" style={{ color: '#6B7280' }}>No booking enquiries yet</p>
          </div>
        ) : (
          <div className="space-y-4">
            {bookings.map((booking, index) => (
              <motion.div
                key={booking.booking_id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                className="bg-white rounded-3xl p-6 shadow-sm hover:shadow-md transition-shadow"
                data-testid={`booking-card-${index}`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-3">
                      {booking.status === 'new' && (
                        <span
                          className="px-3 py-1 rounded-full text-xs font-bold"
                          style={{ backgroundColor: '#B8E5D2', color: '#2F5F5F' }}
                        >
                          NEW
                        </span>
                      )}
                      <span
                        className="px-3 py-1 rounded-full text-xs font-bold text-white"
                        style={{ backgroundColor: getServiceColor(booking.service_type) }}
                      >
                        {booking.service_type}
                      </span>
                    </div>
                    
                    <h3 className="text-xl font-bold mb-2" style={{ fontFamily: 'Nunito, sans-serif', color: '#2F5F5F' }}>
                      {booking.customer_name}
                    </h3>
                    
                    <div className="space-y-2 text-sm" style={{ color: '#6B7280' }}>
                      <div className="flex gap-2">
                        <span className="font-medium min-w-[100px]">Phone:</span> 
                        <span className="font-mono">{booking.customer_phone}</span>
                      </div>
                      {booking.dog_name && (
                        <div className="flex gap-2">
                          <span className="font-medium min-w-[100px]">Dog's Name:</span> 
                          <span>{booking.dog_name}</span>
                        </div>
                      )}
                      <div className="flex gap-2">
                        <span className="font-medium min-w-[100px]">Dog Breed:</span> 
                        <span>{booking.dog_breed}</span>
                      </div>
                      <div className="flex gap-2">
                        <span className="font-medium min-w-[100px]">Date:</span> 
                        <span>{formatDate(booking.booking_date)}</span>
                      </div>
                      <div className="flex gap-2">
                        <span className="font-medium min-w-[100px]">Time:</span> 
                        <span>{booking.time_slot}</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex gap-2">
                    {booking.status === 'new' && (
                      <button
                        onClick={() => handleMarkViewed(booking.booking_id)}
                        data-testid={`mark-viewed-button-${index}`}
                        className="px-4 py-2 rounded-full font-medium transition-all hover:shadow-md flex items-center gap-2"
                        style={{ backgroundColor: '#5AC5C1', color: 'white' }}
                      >
                        <Eye className="w-4 h-4" />
                        Mark Viewed
                      </button>
                    )}
                    <button
                      onClick={() => handleDeleteBooking(booking.booking_id)}
                      data-testid={`delete-booking-button-${index}`}
                      className="px-4 py-2 rounded-full font-medium transition-all hover:shadow-md flex items-center gap-2"
                      style={{ backgroundColor: '#F5C1C1', color: 'white' }}
                    >
                      <Trash2 className="w-4 h-4" />
                      Delete
                    </button>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>
      
      <PWAInstallPrompt />
    </div>
  );
}
